<template>
  <div class="p-4">
    <Spinner v-if="loading" />
    <div v-else-if="error" class="alert alert-danger">{{ error }}</div>
    <div v-else-if="currentProjects.length">
      <table class="table table-striped table-hover">
        <thead>
          <tr>
            <th>{{ i18n.t("Project") }}</th>
            <th>{{ i18n.t("Date & Time") }}</th>
            <th>{{ i18n.t("Associated test") }}</th>
            <th>{{ i18n.t("Department") }}</th>
            <th>{{ i18n.t("Software") }}</th>
            <th v-if="!showPrescriptionProjects">{{ i18n.t("Prescription") }}</th>
            <th>{{ i18n.t("Download") }}</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="file in paginatedProjects" :key="file.key">
            <td><small>{{ truncate(file.file_name, 20) }}</small></td>
            <td><small>{{ getFormattedDateTime(file.file_name) }}</small></td>
            <td><small>{{ truncate(file.test_name, 30) }}</small></td>
            <td><small>{{ file.department }}</small></td>
            <td><small>{{ file.software}}</small></td>
            <td v-if="!showPrescriptionProjects"><small>{{ file.prescription_number_text }}</small></td>
            <td>
              <img
                class="invert cursor-pointer"
                src="@/assets/icons/download.svg"
                width="20"
                height="20"
                alt="download"
                @click="handleDownload(file.id)"
              />
            </td>
          </tr>
        </tbody>
      </table>
      <Pagination
        v-if="totalPages > 1"
        :array="currentProjects"
        :pageNumber="currentPage"
        :totalPages="totalPages"
        @increment="currentPage++"
        @decrement="currentPage--"
      />
    </div>
    <div v-else class="alert alert-info">Nessun progetto CAD trovato.</div>
    <button
      v-if="showPrescriptionProjects || customerCadProjects.length"
      class="btn btn-violet text-uppercase mt-4"
      @click="toggleProjects"
    >
      <img class="me-2 invert" src="@/assets/icons/chevron-down.svg" alt="chevron-down" />
      {{ showPrescriptionProjects ? 'Mostra progetti del paziente' : 'Mostra progetti della prova corrente' }}
    </button>
  </div>
</template>

<script>
import { ref, onMounted, computed, watch } from 'vue';
import { fetchProjects } from '@/use/repositories/projects/fetchProjects';
import { downloadProject } from '@/use/repositories/projects/downloadProject';
import Spinner from '@/components/general/Spinner.vue';
import Pagination from '@/components/general/Pagination.vue';
import { useI18n } from 'vue-i18n';
import { success } from '@/use/toast/success';
import { error as danger } from '@/use/toast/error';
import { dateFormatter } from '@/use/utilities/time/dateFormatter.js';

export default {
  name: 'CADProjects',
  props: {
    currentTestId: { type: [Number], required: true },
    customerId: { type: [Number], required: true },
  },
  components: { Spinner, Pagination },
  setup(props) {
    const cadProjects = ref([]);
    const customerCadProjects = ref([]);
    const loading = ref(false);
    const error = ref(null);
    const currentPage = ref(1);
    const itemsPerPage = 10;
    const showPrescriptionProjects = ref(true);
    const i18n = useI18n();

    const transformProjects = (projects) =>
      projects.map((project) => ({
        ...project,
        key: `${project.file_name}-${project.created_at}`,
      }));

    const loadProjects = async (type, id, targetRef) => {
      loading.value = true;
      error.value = null;
      try {
        const { projects } = await fetchProjects(type, id, null, 0);
        targetRef.value = transformProjects(projects);
      } catch (err) {
        error.value = i18n.t('Error loading CAD projects');
        console.error(err);
      } finally {
        loading.value = false;
      }
    };

    const toggleProjects = () => {
      showPrescriptionProjects.value = !showPrescriptionProjects.value;
      if (!showPrescriptionProjects.value && !customerCadProjects.value.length) {
        loadProjects('by_customer_id', props.customerId, customerCadProjects);
      }
      currentPage.value = 1;
    };

    const getFormattedDateTime = (fileName) => {
      if (!fileName) return 'non associata';
      const dateTimeMatch = fileName.match(/^(\d{4})(\d{2})(\d{2})_(\d{2})(\d{2})/);
      if (!dateTimeMatch) return 'formato non valido';
      const [, year, month, day, hour, minute] = dateTimeMatch;
      const date = new Date(year, month - 1, day, hour, minute);
      
      return dateFormatter(date, { iso: false, dash: false }) || 'formato non valido';
    };

    const truncate = (str, num) =>
      str && str.length > num ? str.slice(0, num) + '...' : str;

    const handleDownload = async (projectId) => {
      try {
        const { downloadUrl, fileName } = await downloadProject(projectId);
        const link = document.createElement('a');
        link.href = downloadUrl;
        link.download = fileName;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        success(i18n.t('Project downloaded successfully'));
      } catch (err) {
        error.value = i18n.t('Error downloading project');
        danger(err);
      }
    };

    const currentProjects = computed(() =>
      showPrescriptionProjects.value ? cadProjects.value : customerCadProjects.value
    );

    const sortedProjects = computed(() =>
      [...currentProjects.value].sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
    );

    const totalPages = computed(() =>
      Math.ceil(sortedProjects.value.length / itemsPerPage) || 1
    );

    const paginatedProjects = computed(() => {
      const start = (currentPage.value - 1) * itemsPerPage;
      return sortedProjects.value.slice(start, start + itemsPerPage);
    });

    watch(totalPages, (newTotal) => {
      if (currentPage.value > newTotal) currentPage.value = newTotal;
    });

    onMounted(() => loadProjects('by_current_test_id', props.currentTestId, cadProjects));

    return {
      loading,
      error,
      currentPage,
      totalPages,
      paginatedProjects,
      showPrescriptionProjects,
      currentProjects,
      cadProjects,
      customerCadProjects,
      toggleProjects,
      getFormattedDateTime,
      truncate,
      handleDownload,
      i18n,
    };
  },
};
</script>

<style scoped lang="scss">
@import '../scss/abstracts/_variables.scss';
.invert {
  filter: invert(1);
}
</style>
